
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Tenant } from '../../../core/types/tenant';
import { Menu, X } from 'lucide-react';

interface TenantLayoutProps {
  tenant: Tenant;
  children: React.ReactNode;
}

import { useAuth } from '../../../modules/auth/AuthContext';

export const TenantLayout: React.FC<TenantLayoutProps> = ({ tenant, children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  // Default navigation if none provided (e.g. legacy or not configured)
  const navigation = tenant.navigation || [
    { id: 'home', label: 'Início', url: '/' },
  ];

  const primaryColor = tenant.branding?.primaryColor || '#2563eb';
  const logo = tenant.branding?.logoUrl;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="flex-shrink-0 flex items-center gap-3">
                {logo ? (
                  <img className="h-10 w-auto" src={logo} alt={tenant.businessName} />
                ) : (
                  <span className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: primaryColor }}>
                    {tenant.businessName.charAt(0)}
                  </span>
                )}
                <span className="text-xl font-bold text-gray-900">{tenant.businessName}</span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <NavLink
                  key={item.id || item.label}
                  to={item.url}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary'
                        : 'text-gray-500 hover:text-gray-900'
                    }`
                  }
                  style={({ isActive }) => isActive ? { color: primaryColor } : {}}
                >
                  {item.label}
                </NavLink>
              ))}
              
              {/* Action Buttons */}
              <div className="border-l pl-6 ml-2 flex items-center gap-4">
                {!user ? (
                   <a 
                     href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
                     className="text-sm font-bold text-gray-700 hover:text-primary transition-colors"
                   >
                     Entrar
                   </a>
                ) : (
                  tenant.tenantType === 'turma' && (
                    <button 
                      className="bg-primary text-white px-4 py-2 rounded-full text-sm font-bold hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                      style={{ backgroundColor: primaryColor }}
                      onClick={() => alert('Em breve: Fluxo de Solicitação de Entrada')}
                    >
                      Participar
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
             <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navigation.map((item) => (
                <NavLink
                  key={item.id || item.label}
                  to={item.url}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md text-base font-medium ${
                      isActive
                        ? 'bg-gray-50 text-primary'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                  style={({ isActive }) => isActive ? { color: primaryColor } : {}}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             <div>
                <h3 className="text-xl font-bold mb-4">{tenant.businessName}</h3>
                <p className="text-gray-400 text-sm">{tenant.description}</p>
             </div>
             <div>
                <h3 className="text-lg font-semibold mb-4">Contato</h3>
                <ul className="space-y-2 text-gray-400 text-sm">
                   {tenant.email && <li>{tenant.email}</li>}
                   {tenant.phone && <li>{tenant.phone}</li>}
                   {tenant.address && (
                     <li>
                       {tenant.address.city}, {tenant.address.state}
                     </li>
                   )}
                </ul>
             </div>
             <div>
                <h3 className="text-lg font-semibold mb-4">Links</h3>
                 <ul className="space-y-2 text-gray-400 text-sm">
                   {navigation.map(item => (
                      <li key={item.id}><a href={item.url} className="hover:text-white transition">{item.label}</a></li>
                   ))}
                </ul>
             </div>
          </div>
          <div className="mt-8 border-t border-gray-800 pt-8 text-center">
            <p className="text-base text-gray-400">
              &copy; {new Date().getFullYear()} {tenant.businessName}. Todos os direitos reservados.
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Powered by <a href="https://nossapesca.com" className="hover:text-gray-400">Nossa Pesca</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
